<template>
  <div class="image-crop-backdrop" @click.self="onCancel">
    <div class="image-crop-dialog">
      <div class="image-crop-header">
        <h6 class="mb-0">Recortar imagen manualmente</h6>
        <button type="button" class="btn-close" @click="onCancel"></button>
      </div>

      <div class="image-crop-body">
        <p class="field-hint mb-2">
          Arrastrá las esquinas para ajustar el recorte, o el centro para moverlo.
          Este recorte se guarda tal cual — no se le vuelve a aplicar la limpieza
          automática de fondo.
        </p>
        <div v-if="loadError" class="alert alert-danger py-2 px-3 mb-2">{{ loadError }}</div>
        <div v-else class="image-crop-canvas-wrap">
          <canvas
            ref="canvas"
            @pointerdown="onPointerDown"
            @pointermove="onPointerMove"
            @pointerup="onPointerUp"
            @pointercancel="onPointerUp"
          ></canvas>
        </div>
      </div>

      <div class="image-crop-footer">
        <button type="button" class="btn btn-secondary btn-sm" @click="onCancel">Cancelar</button>
        <button
          type="button"
          class="btn btn-primary btn-sm"
          :disabled="!ready || !!loadError"
          @click="onApply"
        >
          Aplicar recorte
        </button>
      </div>
    </div>
  </div>
</template>

<script>
// Herramienta de recorte manual (pedido del usuario 2026-08-02): permite
// recortar a mano una imagen nueva, la foto ya guardada de un producto, o
// una candidata sugerida por IA, sin pasar por el procesamiento automático
// de recorte/fondo (imageBackgroundService en el backend) — el operador
// decide el encuadre en vez del sistema. Sin librería nueva: canvas +
// Pointer Events nativos del navegador, mismo criterio que el resto del
// proyecto ("no usar IA/dependencia nueva sin necesidad real").
const HANDLE_SIZE = 14;
const MAX_CANVAS_SIZE = 560;

export default {
  name: 'ImageCropModal',
  props: {
    imageSrc: { type: String, required: true }
  },
  emits: ['confirm', 'cancel'],
  data() {
    return {
      ready: false,
      loadError: '',
      scale: 1,
      naturalWidth: 0,
      naturalHeight: 0,
      // Rectángulo de recorte en coordenadas de PANTALLA (escaladas), no
      // de la imagen original — se convierte a coordenadas naturales recién
      // al aplicar.
      rect: { x: 0, y: 0, w: 0, h: 0 },
      dragMode: '', // '', 'move', 'nw', 'ne', 'sw', 'se'
      dragStart: { x: 0, y: 0 },
      rectStart: { x: 0, y: 0, w: 0, h: 0 }
    };
  },
  mounted() {
    this.loadImage();
  },
  methods: {
    loadImage() {
      this.loadError = '';
      this.ready = false;
      const img = new Image();
      img.onload = () => {
        this.image = img;
        this.naturalWidth = img.naturalWidth;
        this.naturalHeight = img.naturalHeight;
        this.scale = Math.min(1, MAX_CANVAS_SIZE / Math.max(img.naturalWidth, img.naturalHeight));
        const canvasW = Math.round(img.naturalWidth * this.scale);
        const canvasH = Math.round(img.naturalHeight * this.scale);
        this.$nextTick(() => {
          const canvas = this.$refs.canvas;
          if (!canvas) return;
          canvas.width = canvasW;
          canvas.height = canvasH;
          // Recorte inicial: 90% del área, centrado.
          const w = Math.round(canvasW * 0.9);
          const h = Math.round(canvasH * 0.9);
          this.rect = { x: Math.round((canvasW - w) / 2), y: Math.round((canvasH - h) / 2), w, h };
          this.ready = true;
          this.draw();
        });
      };
      img.onerror = () => {
        this.loadError = 'No se pudo cargar la imagen para recortar.';
      };
      img.src = this.imageSrc;
    },
    draw() {
      const canvas = this.$refs.canvas;
      if (!canvas || !this.image) return;
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(this.image, 0, 0, canvas.width, canvas.height);

      // Máscara oscura fuera del recorte.
      ctx.fillStyle = 'rgba(15, 23, 42, 0.55)';
      ctx.fillRect(0, 0, canvas.width, this.rect.y);
      ctx.fillRect(0, this.rect.y + this.rect.h, canvas.width, canvas.height - this.rect.y - this.rect.h);
      ctx.fillRect(0, this.rect.y, this.rect.x, this.rect.h);
      ctx.fillRect(this.rect.x + this.rect.w, this.rect.y, canvas.width - this.rect.x - this.rect.w, this.rect.h);

      // Borde del recorte.
      ctx.strokeStyle = '#07a7e1';
      ctx.lineWidth = 2;
      ctx.strokeRect(this.rect.x, this.rect.y, this.rect.w, this.rect.h);

      // Handles en las 4 esquinas.
      ctx.fillStyle = '#07a7e1';
      this.cornerPoints().forEach(({ x, y }) => {
        ctx.fillRect(x - HANDLE_SIZE / 2, y - HANDLE_SIZE / 2, HANDLE_SIZE, HANDLE_SIZE);
      });
    },
    cornerPoints() {
      const { x, y, w, h } = this.rect;
      return [
        { key: 'nw', x, y },
        { key: 'ne', x: x + w, y },
        { key: 'sw', x, y: y + h },
        { key: 'se', x: x + w, y: y + h }
      ];
    },
    pointerPos(event) {
      const canvas = this.$refs.canvas;
      const box = canvas.getBoundingClientRect();
      return {
        x: ((event.clientX - box.left) / box.width) * canvas.width,
        y: ((event.clientY - box.top) / box.height) * canvas.height
      };
    },
    hitTestHandle(pos) {
      const points = this.cornerPoints();
      const found = points.find(({ x, y }) => Math.abs(pos.x - x) <= HANDLE_SIZE && Math.abs(pos.y - y) <= HANDLE_SIZE);
      return found ? found.key : '';
    },
    hitTestInside(pos) {
      return pos.x >= this.rect.x && pos.x <= this.rect.x + this.rect.w
        && pos.y >= this.rect.y && pos.y <= this.rect.y + this.rect.h;
    },
    onPointerDown(event) {
      if (!this.ready) return;
      const pos = this.pointerPos(event);
      const handle = this.hitTestHandle(pos);
      if (handle) {
        this.dragMode = handle;
      } else if (this.hitTestInside(pos)) {
        this.dragMode = 'move';
      } else {
        return;
      }
      event.target.setPointerCapture?.(event.pointerId);
      this.dragStart = pos;
      this.rectStart = { ...this.rect };
    },
    onPointerMove(event) {
      if (!this.dragMode) return;
      const canvas = this.$refs.canvas;
      const pos = this.pointerPos(event);
      const dx = pos.x - this.dragStart.x;
      const dy = pos.y - this.dragStart.y;
      let { x, y, w, h } = this.rectStart;

      if (this.dragMode === 'move') {
        x = this.clamp(this.rectStart.x + dx, 0, canvas.width - w);
        y = this.clamp(this.rectStart.y + dy, 0, canvas.height - h);
      } else {
        const minSize = HANDLE_SIZE * 2;
        if (this.dragMode.includes('w')) {
          const newX = this.clamp(this.rectStart.x + dx, 0, this.rectStart.x + this.rectStart.w - minSize);
          w = this.rectStart.w - (newX - this.rectStart.x);
          x = newX;
        }
        if (this.dragMode.includes('e')) {
          w = this.clamp(this.rectStart.w + dx, minSize, canvas.width - this.rectStart.x);
        }
        if (this.dragMode.includes('n')) {
          const newY = this.clamp(this.rectStart.y + dy, 0, this.rectStart.y + this.rectStart.h - minSize);
          h = this.rectStart.h - (newY - this.rectStart.y);
          y = newY;
        }
        if (this.dragMode.includes('s')) {
          h = this.clamp(this.rectStart.h + dy, minSize, canvas.height - this.rectStart.y);
        }
      }

      this.rect = { x, y, w, h };
      this.draw();
    },
    onPointerUp() {
      this.dragMode = '';
    },
    clamp(value, min, max) {
      return Math.min(Math.max(value, min), Math.max(min, max));
    },
    onApply() {
      if (!this.image) return;
      // Convertir el recorte de coordenadas de pantalla (escaladas) a
      // coordenadas de la imagen original antes de extraer.
      const sx = Math.round(this.rect.x / this.scale);
      const sy = Math.round(this.rect.y / this.scale);
      const sw = Math.round(this.rect.w / this.scale);
      const sh = Math.round(this.rect.h / this.scale);

      const output = document.createElement('canvas');
      output.width = sw;
      output.height = sh;
      const ctx = output.getContext('2d');
      ctx.drawImage(this.image, sx, sy, sw, sh, 0, 0, sw, sh);

      this.$emit('confirm', output.toDataURL('image/png'));
    },
    onCancel() {
      this.$emit('cancel');
    }
  }
};
</script>

<style scoped>
.image-crop-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.65);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1080;
  padding: 1rem;
}

.image-crop-dialog {
  background: #ffffff;
  border-radius: 0.5rem;
  max-width: 640px;
  width: 100%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.image-crop-header,
.image-crop-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
}

.image-crop-footer {
  border-top: 1px solid rgba(19, 47, 58, 0.12);
  gap: 0.5rem;
}

.image-crop-header {
  border-bottom: 1px solid rgba(19, 47, 58, 0.12);
}

.image-crop-body {
  padding: 1rem;
  overflow: auto;
}

.image-crop-canvas-wrap {
  display: flex;
  justify-content: center;
}

.image-crop-canvas-wrap canvas {
  max-width: 100%;
  touch-action: none;
  cursor: move;
  border: 1px solid rgba(19, 47, 58, 0.16);
}
</style>
